import React, { useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core'
import EditOutlined from '@material-ui/icons/EditOutlined'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { deleteTenantUser, TenantUserInfo } from '../../redux/actions/dashboard'
import TenantUserDialog from './TenantUserDialog'

export const useTenantTableStyles = makeStyles((theme) => ({
    table: {
        '& td:first-child, & th:first-child': {
            paddingLeft: 0,
        },
        '& td:last-child, & th:last-child': {
            paddingRight: 0,
        },
    },
    actionsCell: {
        width: 1,
        whiteSpace: 'nowrap',
        textAlign: 'right',
    },
    empty: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
}))

const useStyles = makeStyles((theme) => ({
    you: {
        marginLeft: theme.spacing(1),
    },
}))

interface Props {
    users: TenantUserInfo[]
    tenantId?: string
    tenantName?: string
    loadError?: string
    onChanged: () => void
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

export default function TenantUsersTable({ users, tenantId, tenantName, loadError, onChanged, onSuccess, onError }: Props) {
    const classes = useStyles()
    const tableClasses = useTenantTableStyles()
    const { t } = useTranslation()
    const currentUserId: string | undefined = useSelector((state: any) => state.login.value.user.id)

    const [userDialog, setUserDialog] = useState<{ user?: TenantUserInfo }>()
    const [userToDelete, setUserToDelete] = useState<TenantUserInfo>()
    const [deleting, setDeleting] = useState(false)

    const isMe = (user: TenantUserInfo) => Boolean(currentUserId && user.id.toLowerCase() === currentUserId.toLowerCase())

    const handleDelete = async () => {
        if (!userToDelete) return
        setDeleting(true)
        try {
            await deleteTenantUser(userToDelete.id)
            onSuccess(t('dashboard.adminDeleted'))
            setUserToDelete(undefined)
            onChanged()
        } catch {
            onError(t('dashboard.errorDeletingLogin'))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <>
            <Table size="small" className={tableClasses.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('dashboard.adminsTitle')}</TableCell>
                        <TableCell className={tableClasses.actionsCell}>
                            {loadError ? null : (
                                <Button
                                    size="small"
                                    color="primary"
                                    startIcon={<AddIcon fontSize="small" />}
                                    onClick={() => setUserDialog({})}
                                >
                                    {t('dashboard.addAdmin')}
                                </Button>
                            )}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loadError ? (
                        <TableRow>
                            <TableCell colSpan={2} className={tableClasses.empty}>
                                {t('dashboard.errorLoadingTenantUsers')} ({loadError})
                            </TableCell>
                        </TableRow>
                    ) : users.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={2} className={tableClasses.empty}>
                                {t('dashboard.noLogins')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        users.map((user) => (
                            <TableRow key={user.id} hover>
                                <TableCell>
                                    {user.login}
                                    {isMe(user) ? <Chip size="small" label={t('dashboard.you')} className={classes.you} /> : null}
                                </TableCell>
                                <TableCell className={tableClasses.actionsCell}>
                                    {isMe(user) ? null : (
                                        <>
                                            <Tooltip title={t('dashboard.editAdmin') as string}>
                                                <IconButton size="small" onClick={() => setUserDialog({ user })}>
                                                    <EditOutlined fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('dashboard.deleteAdmin') as string}>
                                                <IconButton size="small" onClick={() => setUserToDelete(user)}>
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <TenantUserDialog
                open={Boolean(userDialog)}
                user={userDialog?.user}
                tenantId={tenantId}
                tenantName={tenantName}
                onClose={() => setUserDialog(undefined)}
                onSaved={() => {
                    setUserDialog(undefined)
                    onSuccess(t('dashboard.adminSaved'))
                    onChanged()
                }}
                onError={onError}
            />

            <Dialog open={Boolean(userToDelete)} onClose={() => !deleting && setUserToDelete(undefined)}>
                <DialogTitle>{t('dashboard.deleteAdmin')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('dashboard.deleteAdminConfirm', { login: userToDelete?.login })}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserToDelete(undefined)} disabled={deleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
